import type {
  ProductVariantDto,
  ReturnRequestLineItemDto,
  UpdateLineItemNewVariantDto,
} from "@babackapp/private-api-client/types";
import { normalizeProductId } from "@babackapp/return-variables-registry";

const toOptionValue = (value: unknown): string | null =>
  value == null || value === "" ? null : String(value);

const toPrice = (value: unknown): number | null => {
  const price = parseFloat(String(value ?? ""));
  return Number.isNaN(price) ? null : price;
};

/**
 * Exchange article picked in the catalog. The backend rebuilds the snapshot
 * from Shopify (`variant_id` + `productId`); the other fields are fallback
 * hints, stored only when Shopify cannot resolve the variant, so that stock
 * reservation (by SKU) and exports keep working.
 *
 * `productTitle` is the title of the variant's product when the caller knows
 * it (product picked in the catalog, or the returned line's product for a
 * same-product swap), never the variant's `title`, which is its option label.
 *
 * The product id goes through `normalizeProductId`, the same rule as the
 * backend: a stringified missing id (`"null"`), a gid or a non-Shopify id never
 * reaches Shopify as is.
 */
export function newVariantRequestFromVariant(
  variant: ProductVariantDto,
  productTitle?: string | null,
): UpdateLineItemNewVariantDto {
  const options = variant.selectedOptions ?? [];
  return {
    variant_id: variant.id,
    productId: normalizeProductId(variant.product_id),
    productTitle: productTitle?.trim() || null,
    sku: variant.sku ?? null,
    barcode: variant.barcode ?? null,
    option1: toOptionValue(options[0]?.value),
    option2: toOptionValue(options[1]?.value),
    option3: toOptionValue(options[2]?.value),
    price: toPrice(variant.price),
    image: variant.image || null,
    // A catalog variant's `image` is its own photo, never the product's.
    isVariantImage: variant.image ? true : null,
  };
}

/**
 * Default exchange target when the merchant switches a line to "exchange"
 * without picking a variant: the purchased article itself. Hints come from
 * the returned line item, whose `title` is the product title.
 */
export function newVariantRequestFromLineItem(
  lineItem: ReturnRequestLineItemDto,
): UpdateLineItemNewVariantDto {
  return {
    variant_id: lineItem.variant.variant_id,
    productId: normalizeProductId(lineItem.product?.productId),
    productTitle: lineItem.title ?? null,
    sku: lineItem.sku ?? null,
    barcode: lineItem.variant.barcode ?? null,
    option1: lineItem.variant.option1 ?? null,
    option2: lineItem.variant.option2 ?? null,
    option3: lineItem.variant.option3 ?? null,
    price: toPrice(lineItem.price),
    image: lineItem.image ?? null,
  };
}
